import { Heart, Sparkles, Gift, ArrowRight, Quote } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

// Parallax scroll hook
function useParallax(speed = 0.5) {
  const [offset, setOffset] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => setOffset(window.pageYOffset);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return offset * speed;
}

// Animation hook, reveals an element when scrolled into view via
// IntersectionObserver. For ancient browsers that don't support IO,
// sections are shown immediately (feature-detection at the top).
function useScrollAnimation() {
  const elementRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      {
        // Shrink the effective viewport by 25% on the bottom so the
        // animation only fires once the section is genuinely in view,
        // not the moment its top edge peeks above the fold.
        rootMargin: '0px 0px -25% 0px',
        threshold: 0.15
      }
    );
    const el = elementRef.current;
    if (el) observer.observe(el);
    return () => {
      if (el) observer.unobserve(el);
    };
  }, []);

  return [elementRef, isVisible];
}

export default function Home() {
  const darkMode = useDarkMode();
  const parallaxOffset = useParallax(0.3);
  const [section1Ref, section1Visible] = useScrollAnimation();
  const [section2Ref, section2Visible] = useScrollAnimation();
  const [section3Ref, section3Visible] = useScrollAnimation();
  const [section4Ref, section4Visible] = useScrollAnimation();
  const [promiseRef, promiseVisible] = useScrollAnimation();

  useDocumentMeta({
    title: 'Mementa, Thoughtful Gifts, Beautifully Saved',
    description:
      'Build wishlists, save keepsakes, and share gift ideas with the people you love. Never wonder what to give again.',
    url: 'https://mementa.gifts/',
  });

  return (
    <div className="transition-colors page-enter" style={{ background: darkMode.bgPrimary, overflowX: 'hidden' }}>
      
      {/* Hero - Full bleed image with overlay */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Parallax */}
        <div 
          className="absolute inset-0 z-0"
          style={{ transform: `translateY(${parallaxOffset}px)`, background: 'linear-gradient(135deg, #9E6752 0%, #534145 100%)' }}
        >
          <img 
            src="/about/hero.jpg" 
            alt="Wrapped gifts"
            className="w-full h-[120%] object-cover"
            loading="eager"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <div 
            className="absolute inset-0" 
            style={{ background: 'linear-gradient(to bottom, rgba(32,33,43,0.6) 0%, rgba(32,33,43,0.85) 100%)' }} 
          />
        </div>
        
        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <p 
            className="text-xs tracking-[0.3em] mb-6 page-rise-1"
            style={{ color: '#FED7A5' }}
          >
            THE STORY BEHIND THE GIFT
          </p>
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl mb-6 leading-[1.1] page-rise-2"
            style={{ fontFamily: 'Playfair Display, serif', color: 'white', fontWeight: '700' }}
          >
            We believe in gifts<br />
            that{' '}
            <span style={{ fontFamily: 'Great Vibes, cursive', fontWeight: '400', color: '#FED7A5', fontSize: '1.15em' }}>
              stay
            </span>
          </h1>
          <p 
            className="text-lg sm:text-xl leading-relaxed max-w-xl mx-auto page-rise-3"
            style={{ color: 'rgba(255,255,255,0.9)', fontWeight: '300' }}
          >
            The ones people keep on their shelf, not in a drawer.
            The ones that still mean something years later.
          </p>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-5 h-8 rounded-full border-2 border-white/40 flex items-start justify-center p-1.5">
            <div className="w-1 h-1.5 rounded-full bg-white/70" />
          </div>
        </div>
      </section>

      {/* The Name Origin - Tighter split section */}
      <section ref={section1Ref}>
        <div className="grid lg:grid-cols-2">
          {/* Left - Image */}
          <div 
            className="relative h-[45vh] lg:h-[70vh] overflow-hidden"
            style={{
              opacity: section1Visible ? 1 : 0,
              transform: section1Visible ? 'translateX(0)' : 'translateX(-40px)',
              transition: 'all 2.2s cubic-bezier(0.16, 1, 0.3, 1)',
              background: 'linear-gradient(135deg, #E8B4B8 0%, #D4A4A8 100%)'
            }}
          >
            <img 
              src="/about/feature-1.jpg" 
              alt="Thoughtful gift"
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(158,103,82,0.2) 0%, transparent 100%)' }} />
          </div>
          
          {/* Right - Content */}
          <div 
            className="flex items-center px-8 lg:px-14 py-12 lg:py-0"
            style={{ background: '#FFFBF7' }}
          >
            <div
              style={{
                opacity: section1Visible ? 1 : 0,
                transform: section1Visible ? 'translateX(0)' : 'translateX(40px)',
                transition: 'all 2.2s cubic-bezier(0.16, 1, 0.3, 1) 0.3s'
              }}
            >
              <p className="text-xs tracking-[0.2em] mb-4" style={{ color: '#9E6752' }}>THE NAME</p>
              <h2 
                className="text-3xl sm:text-4xl mb-6"
                style={{ fontFamily: 'Playfair Display, serif', color: '#20212B', fontWeight: '700' }}
              >
                Mementa
              </h2>
              <p className="text-lg leading-relaxed mb-4" style={{ color: '#534145' }}>
                From <em>memento</em>-something kept as a reminder.
              </p>
              <p className="text-base leading-relaxed mb-4" style={{ color: '#73766A' }}>
                We started with a simple question: Why do some gifts end up forgotten 
                while others become treasured possessions?
              </p>
              <p className="text-base leading-relaxed" style={{ color: '#73766A' }}>
                The answer isn't price. It's resonance-finding something that says{' '}
                <span style={{ color: '#534145', fontStyle: 'italic' }}>"I know you."</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Break - More compact */}
      <section 
        ref={section2Ref}
        className="py-16 sm:py-20 relative overflow-hidden"
        style={{ background: '#2D4354' }}
      >
        {/* Decorative elements */}
        <div className="absolute top-6 left-6 w-20 h-20 rounded-full opacity-10" style={{ background: '#FED7A5' }} />
        <div className="absolute bottom-6 right-6 w-28 h-28 rounded-full opacity-10" style={{ background: '#FED7A5' }} />
        
        <div 
          className="max-w-3xl mx-auto px-4 text-center relative z-10"
          style={{
            opacity: section2Visible ? 1 : 0,
            transform: section2Visible ? 'scale(1)' : 'scale(0.92)',
            transition: 'all 2s cubic-bezier(0.16, 1, 0.3, 1)'
          }}
        >
          <Quote className="w-10 h-10 mx-auto mb-5 opacity-30" style={{ color: '#FED7A5' }} />
          <blockquote 
            className="text-2xl sm:text-3xl leading-snug"
            style={{ fontFamily: 'Playfair Display, serif', color: 'white', fontWeight: '400' }}
          >
            The best gifts become{' '}
            <span style={{ fontFamily: 'Great Vibes, cursive', color: '#FED7A5', fontSize: '1.1em' }}>souvenirs</span>
            {' '}of relationships-keepsakes that hold meaning long after they're unwrapped.
          </blockquote>
        </div>
      </section>

      {/* What We Do - Tighter grid */}
      <section 
        ref={section3Ref}
        className="py-16 sm:py-20"
        style={{ background: '#FFFBF7' }}
      >
        <div className="max-w-6xl mx-auto px-4">
          <div 
            className="text-center mb-10"
            style={{
              opacity: section3Visible ? 1 : 0,
              transform: section3Visible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 2s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
          >
            <p className="text-xs tracking-[0.2em] mb-3" style={{ color: '#9E6752' }}>WHAT WE DO</p>
            <h2 
              className="text-3xl sm:text-4xl"
              style={{ fontFamily: 'Playfair Display, serif', color: '#20212B', fontWeight: '700' }}
            >
              Making it{' '}
              <span style={{ fontFamily: 'Great Vibes, cursive', color: '#9E6752', fontWeight: '400', fontSize: '1.1em' }}>effortless</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { 
                img: '/about/card-gift.jpg',
                icon: Heart,
                title: 'You tell us about them',
                desc: 'Their quirks, passions, what makes them laugh.',
                delay: '0s'
              },
              { 
                img: '/about/card-time.jpg',
                icon: Sparkles,
                title: 'We find the match',
                desc: 'Gifts that actually feel right for who they are.',
                delay: '0.1s'
              },
              { 
                img: '/about/card-eyes.jpg',
                icon: Gift,
                title: 'You give with confidence',
                desc: 'No more "I hope they like it." You\'ll know.',
                delay: '0.2s'
              }
            ].map((card, idx) => {
              const Icon = card.icon;
              return (
                <div 
                  key={card.title}
                  className="group cursor-pointer"
                  style={{
                    opacity: section3Visible ? 1 : 0,
                    transform: section3Visible ? 'translateY(0)' : 'translateY(40px)',
                    transition: `all 2s cubic-bezier(0.16, 1, 0.3, 1) ${card.delay}`
                  }}
                >
                  <div className="relative h-56 rounded-2xl overflow-hidden mb-4" style={{ background: 'linear-gradient(135deg, #9E6752 0%, #534145 100%)' }}>
                    <img 
                      src={card.img} 
                      alt={card.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    <div 
                      className="absolute inset-0"
                      style={{ background: 'linear-gradient(to top, rgba(32,33,43,0.7) 0%, transparent 50%)' }}
                    />
                    {/* Icon badge */}
                    <div 
                      className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: 'rgba(254,215,165,0.9)' }}
                    >
                      <Icon className="w-5 h-5" style={{ color: '#20212B' }} />
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 
                        className="text-lg"
                        style={{ fontFamily: 'Playfair Display, serif', color: 'white', fontWeight: '600' }}
                      >
                        {card.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: '#73766A' }}>
                    {card.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats/Trust Bar - New compact section */}
      <section className="py-10" style={{ background: '#534145' }}>
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-3 gap-8 text-center">
            {[
              { number: '2,500+', label: 'Curated Gifts' },
              { number: '50+', label: 'Categories' },
              { number: '100%', label: 'Thoughtfully Chosen' }
            ].map((stat, idx) => (
              <div key={stat.label}>
                <p className="text-2xl sm:text-3xl font-bold mb-1" style={{ color: '#FED7A5' }}>{stat.number}</p>
                <p className="text-xs tracking-wide" style={{ color: 'rgba(255,255,255,0.7)' }}>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Promise - Founder's note */}
      <section ref={promiseRef} className="py-16 sm:py-20" style={{ background: '#FFF8F0' }} data-testid="about-promise-section">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-5 gap-8 lg:gap-12 items-center">
            <div
              className="md:col-span-2"
              style={{
                opacity: promiseVisible ? 1 : 0,
                transform: promiseVisible ? 'translateX(0)' : 'translateX(-30px)',
                transition: 'all 2s cubic-bezier(0.16, 1, 0.3, 1)'
              }}
            >
              <div className="relative rounded-2xl overflow-hidden" style={{ boxShadow: '0 20px 50px rgba(83,65,69,0.18)', background: 'linear-gradient(135deg, #E8B4B8 0%, #D4A4A8 100%)' }}>
                <img
                  src="/about/promise.jpg"
                  alt="Writing a heartfelt gift note"
                  className="w-full h-72 md:h-80 object-cover"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
            </div>
            <div
              className="md:col-span-3"
              style={{
                opacity: promiseVisible ? 1 : 0,
                transform: promiseVisible ? 'translateX(0)' : 'translateX(30px)',
                transition: 'all 2s cubic-bezier(0.16, 1, 0.3, 1) 0.25s'
              }}
            >
              <p className="text-xs tracking-[0.2em] mb-3" style={{ color: '#9E6752' }}>OUR PROMISE</p>
              <h2 className="text-3xl sm:text-4xl mb-5" style={{ fontFamily: 'Playfair Display, serif', color: '#20212B', fontWeight: '700' }}>
                Chosen like it's for{' '}
                <span style={{ fontFamily: 'Great Vibes, cursive', color: '#9E6752', fontWeight: '400', fontSize: '1.1em' }}>family</span>
              </h2>
              <p className="text-base leading-relaxed mb-4" style={{ color: '#534145' }}>
                Every gift on Mementa is hand-picked, never auto-scraped. If we wouldn't
                give it to someone we love, it doesn't make the shelf.
              </p>
              <p className="text-base leading-relaxed mb-6" style={{ color: '#73766A' }}>
                Behind every recommendation is a simple test: will this still mean something
                a year from now? That's the promise, gifts worth keeping, from people who care.
              </p>
              <p style={{ fontFamily: 'Great Vibes, cursive', fontSize: '2rem', color: '#9E6752', lineHeight: 1 }}>
                Devon J.
              </p>
              <p className="text-xs mt-1 tracking-wide" style={{ color: '#73766A' }}>FOUNDER, MEMENTA</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - More compact */}
      <section 
        ref={section4Ref}
        className="relative py-20 sm:py-24 overflow-hidden"
      >
        {/* Background, gradient fallback so the section reads correctly
            even when the Unsplash image is blocked by a network/extension. */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #73766A 0%, #2D4354 100%)' }}>
          <img 
            src="/about/cta-bg.jpg" 
            alt="Gift giving moment"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(115,118,106,0.92) 0%, rgba(45,67,84,0.92) 100%)' }} />
        </div>
        
        {/* Content */}
        <div 
          className="relative z-10 max-w-2xl mx-auto px-4 text-center"
          style={{
            opacity: section4Visible ? 1 : 0,
            transform: section4Visible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 2s cubic-bezier(0.16, 1, 0.3, 1)'
          }}
        >
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl mb-4 leading-tight"
            style={{ fontFamily: 'Playfair Display, serif', color: 'white', fontWeight: '700' }}
          >
            Ready to find{' '}
            <span style={{ fontFamily: 'Great Vibes, cursive', color: '#FED7A5', fontWeight: '400', fontSize: '1.1em' }}>the one</span>?
          </h2>
          <p className="text-lg mb-8" style={{ color: 'rgba(255,255,255,0.85)' }}>
            The gift they'll actually keep.
          </p>
          <Link to="/">
            <Button
              size="lg"
              className="group"
              style={{
                background: '#FED7A5', 
                color: '#20212B', 
                borderRadius: '30px', 
                padding: '16px 40px', 
                fontSize: '16px',
                fontWeight: '600',
                boxShadow: '0 8px 30px rgba(254,215,165,0.3)'
              }}
            >
              Start Exploring
              <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
